import React, { useEffect, useMemo, useState } from 'react';
import DefaultTextField from 'components/Utils/DefaultTextField';
import ATable from 'components/Clinic/ClinicFinancials/ATable';
import moment from 'moment-timezone';
import _ from 'lodash';
import { FancyAPlot } from 'components/Clinic/ClinicFinancials/APlot';
import ReportingWorker from '../../../workers/reporting.worker?worker';
import LoadingIndicator from './LoadingIndicator';
import { useAppSelector } from 'careand-redux/store';
import ErrorBoundary from 'components/Utils/ErrorBoundary';

const FORMAT = 'YYYY-MM';

export const ReportingData = ({ invoices, subscriptions, range }) => {
  const [costs, setCosts] = useState('5000');
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const clinics = useAppSelector((state) => state.resource.clinics);

  const callWebWorker = (worker, invoices, subscriptions, costs, clinics, range) => {
    setLoading(true);
    worker.onmessage = (event) => {
      setData(event.data);
      setLoading(false);
    };
    worker.postMessage({ invoices, subscriptions, costs, clinics, range });
  };

  useEffect(() => {
    if (clinics?.length > 0) {
      const worker = new ReportingWorker();
      callWebWorker(worker, invoices, subscriptions, costs, clinics, range);
      return () => {
        worker.terminate();
      };
    }
  }, [invoices, subscriptions, costs, clinics, range]);

  return (
    <div className="reporting-stats">
      {loading && <LoadingIndicator hasData={!!data} />}
      {data && (
        <>
          <div className="plots">
            <ReportingTable
              invoices={invoices}
              costs={costs}
              setCosts={setCosts}
              data={data}
              clinics={clinics}
            />
            <ReportingPlot
              subscriptions={subscriptions}
              data={data}
              clinics={clinics}
              range={range}
            />
          </div>
        </>
      )}
    </div>
  );
};

export const ReportingTable = ({ invoices, costs, setCosts, data, clinics }) => {
  const { headers, rows } = useMemo(
    () => getTableData(invoices, data, clinics),
    [invoices, data, clinics],
  );

  return (
    <div className="col">
      <div>
        <DefaultTextField
          name="costs"
          label="Monthly Acquisition Costs"
          value={costs}
          onChange={(event) => setCosts(event.target.value)}
          style={{ width: '200px' }}
        />
      </div>
      <ATable headers={headers} rows={rows} />
    </div>
  );
};

function getTableData(invoices, data, clinics) {
  const headers = ['', 'Last 12 Months', 'Last Month'];
  const clinicsTitles = clinics.map(
    (clinic) => `Churn Rate (${clinic.name.split('Care& ').join('')})`,
  );

  const {
    mrrData,
    arpaData,
    arpsData,
    cacSubsData,
    cacPPUData,
    cacTotalData,
    churnData,
    ltvData,
    upgradeData,
    conversionData,
    subscriberGrowthData,
    clinicsChurn,
  } = data;

  invoices = _.uniqBy(invoices, 'id');

  if (!invoices || invoices.length === 0) return { headers, rows: [] };

  const rows = [
    ['ARR/MRR', mrrData.thisYear, mrrData.lastMonth],
    ['ARPA (overall)', arpaData.thisYear, arpaData.lastMonth],
    ['ARPA (monthly, subs)', arpsData.thisYear, arpsData.lastMonth],
    ['Subscriber Monthly Growth', subscriberGrowthData.thisYear, subscriberGrowthData.lastMonth],
    ['Upgrade Rate (Sub from PPU)', upgradeData.thisYear, upgradeData.lastMonth],
    ['PPU to Sub Conversion Rate', conversionData.thisYear, conversionData.lastMonth],
    ['Churn Rate', churnData.thisYear, churnData.lastMonth],
    ...clinicsChurn.map((row, index) => [clinicsTitles[index], row.thisYear, row.lastMonth]),
    ['LTV (subs)', ltvData.thisYear, ltvData.lastMonth],
    ['CAC (subs)', cacSubsData.thisYear, cacSubsData.lastMonth],
    ['CAC (PPU)', cacPPUData.thisYear, cacPPUData.lastMonth],
    ['CAC (total)', cacTotalData.thisYear, cacTotalData.lastMonth],
  ];

  const PERCENTAGE_KEYS = [
    'Churn Rate',
    ...clinicsTitles,
    'Upgrade Rate (Sub from PPU)',
    'PPU to Sub Conversion Rate',
    'Subscriber Monthly Growth',
  ];

  rows.forEach((row) => {
    for (let i = 1; i < row.length; i++) {
      if (!PERCENTAGE_KEYS.includes(row[0])) {
        row[i] =
          row[i] !== undefined
            ? new Intl.NumberFormat('en-CA', {
                style: 'currency',
                currency: 'CAD',
              }).format(row[i] / 100)
            : 'N/A';
      } else {
        row[i] = row[i] !== undefined ? row[i] + '%' : 'N/A';
      }
    }
  });
  return { headers, rows };
}

export const ReportingPlot = ({ data, clinics, range }) => {
  const clinicsTitles = clinics.map(
    (clinic) => `Churn Rate (${clinic.name.split('Care& ').join('')})`,
  );

  const charts = useMemo(() => {
    if (data.mrrData.months.length === 0) return undefined;

    const lines = [];

    function getLine(data, title, divideBy100 = true, format = true, noTotal = false) {
      let byPeriod = {};

      Object.keys(data.months).forEach((monthStr) => {
        let periodFormat = moment(monthStr, FORMAT).endOf('month').format(range.unitFormat);
        if (divideBy100) byPeriod[periodFormat] = data.months[monthStr] / 100.0;
        else byPeriod[periodFormat] = data.months[monthStr];
      });

      return range.ranges.map(({ start, end }) => {
        let data = _.sortBy(Object.keys(byPeriod))
          .filter((month) => {
            let monthUnix = moment(month, range.unitFormat).endOf('month').unix(); // Ensure comparison range is also normalized
            return monthUnix >= start && monthUnix < end;
          })
          .map((month, index) => {
            return {
              x: index,
              y: byPeriod[month] || 0,
              text: month,
            };
          });
        return {
          append: !format ? '%' : undefined,
          format,
          label: title,
          data,
          noTotal,
        };
      });
    }
    lines.push(getLine(data.mrrData, 'MRR', undefined, undefined, true));
    lines.push(getLine(data.arpaData, 'ARPA (overall)', undefined, undefined, true));
    lines.push(getLine(data.arpsData, 'ARPA (subs)', undefined, undefined, true));
    lines.push(getLine(data.cacSubsData, 'CAC (subs)', undefined, undefined, true));
    lines.push(getLine(data.cacPPUData, 'CAC (PPU)', undefined, undefined, true));
    lines.push(getLine(data.cacTotalData, 'CAC (total)', undefined, undefined, true));
    lines.push(getLine(data.ltvData, 'LTV (subs)', undefined, undefined, true));
    lines.push(getLine(data.churnData, 'Churn Rate', false, false, true));
    data.clinicsChurn.forEach((clinic, index) => {
      lines.push(getLine(clinic, clinicsTitles[index], false, false, true));
    });
    lines.push(getLine(data.upgradeData, 'Upgrade Rate (Sub from PPU)', false, false, true));
    lines.push(getLine(data.conversionData, 'PPU to Sub Conversion Rate', false, false, true));

    return lines;
  }, [data, range]);

  return (charts || []).map((datasets, index) => {
    return (
      <ErrorBoundary key={index + range?.name + datasets?.[0].data?.length}>
        <FancyAPlot
          title={datasets[0].label}
          datasets={datasets}
          key={index + range?.name}
          noTotal={datasets[0].noTotal}
        />
      </ErrorBoundary>
    );
  });
};
